import { Head, router, useForm } from "@inertiajs/react"
import { ArrowLeft, ArrowRight, Check } from "lucide-react"
import { useState } from "react"

import DatePicker from "@/components/date-picker"
import LocationSelect from "@/components/location-select"
import PositionPicker from "@/components/position-picker"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { TryoutsRegistrationsNew } from "@/types/serializers"
import type RequestsCreateTryoutRegistration from "@/types/serializers/RequestsCreateTryoutRegistration"

const FEET = [
  { value: "derecho", label: "Derecho" },
  { value: "izquierdo", label: "Izquierdo" },
  { value: "ambidiestro", label: "Ambidiestro" },
]

function formatTime(timeStr: string): string {
  const match = timeStr.match(/T(\d{2}:\d{2})/)
  return match ? match[1] : timeStr.slice(0, 5)
}

export default function TryoutRegistrationNew({
  tryout,
  categories,
  authenticated,
  has_player_profile,
  player_birth_year,
}: TryoutsRegistrationsNew) {
  const [step, setStep] = useState(has_player_profile ? 1 : 0)
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({})

  const { data, setData, post, processing, errors } =
    useForm<RequestsCreateTryoutRegistration>({
      registration: {
        email: null,
        first_name: null,
        last_name: null,
        password: null,
        password_confirmation: null,
        date_of_birth: null,
        position: null,
        dominant_foot: null,
        country: null,
        city: null,
        region: null,
        height_cm: null,
        current_club: null,
        guardian_name: null,
        guardian_phone: null,
        tryout_category_id: categories[0]?.id ?? 0,
      },
    })

  const setField = (
    field: keyof typeof data.registration,
    value: string | number | null,
  ) =>
    setData("registration", {
      ...data.registration,
      [field]: value,
    } as typeof data.registration)

  // Determine the player's birth year for category validation
  // For existing players use server-provided value; for new registrations derive from form input
  const playerBirthYear: number | null = has_player_profile
    ? player_birth_year
    : data.registration.date_of_birth
      ? new Date(data.registration.date_of_birth + "T00:00:00").getFullYear()
      : null

  const categoryMatchesPlayer = (cat: (typeof categories)[0]) => {
    if (!playerBirthYear) return true
    return (
      playerBirthYear >= cat.birth_year_from &&
      playerBirthYear <= cat.birth_year_to
    )
  }

  function isStepApplicable(s: number): boolean {
    if (s === 0 && has_player_profile) return false
    if (s === 3 && authenticated) return false
    return true
  }

  const applicableSteps = [0, 1, 2, 3, 4].filter(isStepApplicable)
  const totalSteps = applicableSteps.length
  const currentStepIndex = applicableSteps.indexOf(step)
  const progressPercent = ((currentStepIndex + 1) / totalSteps) * 100

  if (currentStepIndex === -1) {
    router.visit(`/pruebas/${tryout.slug}`)
    return null
  }

  function validateStep(): boolean {
    const errs: Record<string, string> = {}
    const r = data.registration

    if (step === 0) {
      if (!r.date_of_birth)
        errs.date_of_birth = "La fecha de nacimiento es obligatoria"
      if (!r.position) errs.position = "La posición es obligatoria"
      if (!r.country?.trim()) errs.country = "El país es obligatorio"
    }

    if (step === 3) {
      if (!r.email?.trim()) errs.email = "El email es obligatorio"
      if (!r.first_name?.trim()) errs.first_name = "El nombre es obligatorio"
      if (!r.last_name?.trim()) errs.last_name = "El apellido es obligatorio"
      if (!r.password || r.password.length < 8)
        errs.password = "La contraseña debe tener al menos 8 caracteres"
      if (r.password !== r.password_confirmation)
        errs.password_confirmation = "Las contraseñas no coinciden"
    }

    // Step 1 (guardian) is optional — no validation needed

    if (step === 2 && playerBirthYear) {
      const selected = categories.find(
        (c) => c.id === data.registration.tryout_category_id,
      )
      if (selected && !categoryMatchesPlayer(selected)) {
        errs.tryout_category_id =
          "La categoría seleccionada no corresponde a tu año de nacimiento"
      }
    }

    setStepErrors(errs)
    return Object.keys(errs).length === 0
  }

  function nextStep() {
    if (!validateStep()) return
    let next = step + 1
    while (next <= 4 && !isStepApplicable(next)) next++
    setStep(next)
  }

  function prevStep() {
    setStepErrors({})
    let prev = step - 1
    while (prev >= 0 && !isStepApplicable(prev)) prev--

    if (prev < 0) {
      router.visit(`/pruebas/${tryout.slug}`)
      return
    }

    setStep(prev)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(`/pruebas/${tryout.slug}/inscripcion`)
  }

  const fieldError = (field: string) =>
    stepErrors[field] || errors[`registration.${field}` as keyof typeof errors]

  const selectedCategory = categories.find(
    (c) => c.id === data.registration.tryout_category_id,
  )

  return (
    <>
      <Head title={`Inscripción: ${tryout.title} | KrownFT`} />

      <div className="bg-background min-h-svh">
        <div className="mx-auto max-w-lg px-4 py-6">
          <div className="mb-2 text-center">
            <h1 className="text-2xl font-bold text-white">Inscripción</h1>
            <p className="text-muted-foreground text-sm">{tryout.title}</p>
          </div>

          <Progress value={progressPercent} className="mb-6" />

          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit}>
                {/* Step 0: Player data */}
                {step === 0 && !has_player_profile && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold">Datos del jugador</h2>
                    <DatePicker
                      label="Fecha de nacimiento"
                      value={data.registration.date_of_birth ?? ""}
                      onChange={(v) => setField("date_of_birth", v)}
                      error={fieldError("date_of_birth")}
                    />

                    <div className="space-y-1.5">
                      <Label>Posición</Label>
                      <PositionPicker
                        value={data.registration.position}
                        onChange={(v) => setField("position", v)}
                      />
                      {fieldError("position") && (
                        <p className="text-sm text-red-500">
                          {fieldError("position")}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label>Pie dominante</Label>
                      <Select
                        value={data.registration.dominant_foot ?? ""}
                        onValueChange={(v) => setField("dominant_foot", v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {FEET.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <LocationSelect
                      country={data.registration.country ?? ""}
                      region={data.registration.region ?? ""}
                      city={data.registration.city ?? ""}
                      onCountryChange={(v) => setField("country", v)}
                      onRegionChange={(v) => setField("region", v || null)}
                      onCityChange={(v) => setField("city", v || null)}
                      showCity={true}
                    />
                    {fieldError("country") && (
                      <p className="text-sm text-red-500">
                        {fieldError("country")}
                      </p>
                    )}

                    <div className="space-y-1.5">
                      <Label htmlFor="height_cm">Altura (cm)</Label>
                      <Input
                        id="height_cm"
                        type="number"
                        value={data.registration.height_cm ?? ""}
                        onChange={(e) =>
                          setField(
                            "height_cm",
                            e.target.value ? parseInt(e.target.value) : null,
                          )
                        }
                        min={150}
                        max={220}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="current_club">Club actual</Label>
                      <Input
                        id="current_club"
                        value={data.registration.current_club ?? ""}
                        onChange={(e) =>
                          setField("current_club", e.target.value || null)
                        }
                        placeholder="Opcional"
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        className="flex-1"
                        onClick={nextStep}
                      >
                        Siguiente
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 1: Guardian */}
                {step === 1 && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold">
                      Datos del tutor (opcional)
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      Si el jugador es menor de edad, podés agregar los datos de
                      un tutor.
                    </p>
                    <div className="space-y-1.5">
                      <Label htmlFor="guardian_name">
                        Nombre completo del tutor
                      </Label>
                      <Input
                        id="guardian_name"
                        value={data.registration.guardian_name ?? ""}
                        onChange={(e) =>
                          setField("guardian_name", e.target.value || null)
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="guardian_phone">Teléfono del tutor</Label>
                      <Input
                        id="guardian_phone"
                        value={data.registration.guardian_phone ?? ""}
                        onChange={(e) =>
                          setField("guardian_phone", e.target.value || null)
                        }
                        placeholder="+54 11 1234-5678"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={prevStep}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Atrás
                      </Button>
                      <Button
                        type="button"
                        className="flex-1"
                        onClick={nextStep}
                      >
                        Siguiente
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Category selection */}
                {step === 2 && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold">
                      Seleccioná tu categoría
                    </h2>
                    <div className="space-y-2">
                      {categories.map((cat) => {
                        const matches = categoryMatchesPlayer(cat)
                        const disabled = cat.full || !matches
                        return (
                          <div
                            key={cat.id}
                            className={`rounded-lg border p-3 transition-colors ${
                              data.registration.tryout_category_id === cat.id
                                ? "border-krown-100 bg-krown-100/10"
                                : "border-border hover:border-krown-grey-50"
                            } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                            onClick={() =>
                              !disabled &&
                              setField("tryout_category_id", cat.id)
                            }
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-semibold">
                                  {cat.label}
                                </span>
                                <p className="text-muted-foreground text-sm">
                                  {new Date(
                                    cat.date + "T00:00:00",
                                  ).toLocaleDateString("es-AR", {
                                    weekday: "long",
                                    day: "numeric",
                                    month: "long",
                                  })}{" "}
                                  — {formatTime(cat.start_time)} a{" "}
                                  {formatTime(cat.end_time)}
                                </p>
                              </div>
                              {data.registration.tryout_category_id ===
                                cat.id && (
                                <Check className="text-krown-100 h-5 w-5" />
                              )}
                              {cat.full && (
                                <span className="text-xs text-red-400">
                                  Completo
                                </span>
                              )}
                              {!cat.full && !matches && (
                                <span className="text-xs text-orange-400">
                                  No corresponde
                                </span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    {(stepErrors.tryout_category_id ||
                      errors["registration.tryout_category_id"]) && (
                      <p className="text-sm text-red-500">
                        {stepErrors.tryout_category_id ||
                          errors["registration.tryout_category_id"]}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={prevStep}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Atrás
                      </Button>
                      <Button
                        type="button"
                        className="flex-1"
                        onClick={nextStep}
                      >
                        Siguiente
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Create account */}
                {step === 3 && !authenticated && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold">Crear cuenta</h2>
                    <div className="space-y-1.5">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={data.registration.email ?? ""}
                        onChange={(e) => setField("email", e.target.value)}
                      />
                      {fieldError("email") && (
                        <p className="text-sm text-red-500">
                          {fieldError("email")}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="first_name">Nombre</Label>
                        <Input
                          id="first_name"
                          value={data.registration.first_name ?? ""}
                          onChange={(e) =>
                            setField("first_name", e.target.value)
                          }
                        />
                        {fieldError("first_name") && (
                          <p className="text-sm text-red-500">
                            {fieldError("first_name")}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="last_name">Apellido</Label>
                        <Input
                          id="last_name"
                          value={data.registration.last_name ?? ""}
                          onChange={(e) =>
                            setField("last_name", e.target.value)
                          }
                        />
                        {fieldError("last_name") && (
                          <p className="text-sm text-red-500">
                            {fieldError("last_name")}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="password">Contraseña</Label>
                      <Input
                        id="password"
                        type="password"
                        value={data.registration.password ?? ""}
                        onChange={(e) => setField("password", e.target.value)}
                      />
                      {fieldError("password") && (
                        <p className="text-sm text-red-500">
                          {fieldError("password")}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="password_confirmation">
                        Confirmar contraseña
                      </Label>
                      <Input
                        id="password_confirmation"
                        type="password"
                        value={data.registration.password_confirmation ?? ""}
                        onChange={(e) =>
                          setField("password_confirmation", e.target.value)
                        }
                      />
                      {fieldError("password_confirmation") && (
                        <p className="text-sm text-red-500">
                          {fieldError("password_confirmation")}
                        </p>
                      )}
                    </div>
                    <p className="text-muted-foreground text-center text-xs">
                      ¿Ya tenés cuenta?{" "}
                      <button
                        type="button"
                        className="text-krown-100 underline"
                        onClick={() => router.visit("/sign_in")}
                      >
                        Iniciá sesión
                      </button>
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={prevStep}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Atrás
                      </Button>
                      <Button
                        type="button"
                        className="flex-1"
                        onClick={nextStep}
                      >
                        Siguiente
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 4: Confirm */}
                {step === 4 && (
                  <div className="space-y-4">
                    <h2 className="text-lg font-semibold">
                      Confirmar inscripción
                    </h2>
                    <div className="border-border space-y-2 rounded-lg border p-4">
                      <p className="text-sm">
                        <span className="text-muted-foreground">Prueba:</span>{" "}
                        {tryout.title}
                      </p>
                      <p className="text-sm">
                        <span className="text-muted-foreground">Sede:</span>{" "}
                        {tryout.venue_name}
                      </p>
                      {selectedCategory && (
                        <>
                          <p className="text-sm">
                            <span className="text-muted-foreground">
                              Categoría:
                            </span>{" "}
                            {selectedCategory.label}
                          </p>
                          <p className="text-sm">
                            <span className="text-muted-foreground">
                              Fecha:
                            </span>{" "}
                            {new Date(
                              selectedCategory.date + "T00:00:00",
                            ).toLocaleDateString("es-AR", {
                              weekday: "long",
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </p>
                          <p className="text-sm">
                            <span className="text-muted-foreground">
                              Horario:
                            </span>{" "}
                            {formatTime(selectedCategory.start_time)} a{" "}
                            {formatTime(selectedCategory.end_time)}
                          </p>
                        </>
                      )}
                      {data.registration.guardian_name && (
                        <p className="text-sm">
                          <span className="text-muted-foreground">Tutor:</span>{" "}
                          {data.registration.guardian_name}
                        </p>
                      )}
                    </div>

                    {(errors as Record<string, string>)[
                      "registration.base"
                    ] && (
                      <p className="text-sm text-red-500">
                        {
                          (errors as Record<string, string>)[
                            "registration.base"
                          ]
                        }
                      </p>
                    )}

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={prevStep}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Atrás
                      </Button>
                      <Button
                        type="submit"
                        className="from-krown-orange-from to-krown-orange-to flex-1 bg-gradient-to-r"
                        disabled={processing}
                      >
                        <Check className="mr-2 h-4 w-4" />
                        Confirmar inscripción
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}
